<template>
    <div class="h-full flex flex-col space-y-4">
        <div class="flex items-center justify-between space-x-2">
            <h1 class="text-2xl font-semibold text-gray-700">Field Posts</h1>
        </div>

        <div class="flex-1 bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
            <UTable
                :rows="rows"
                :columns="columns"
                :loading="loading"
                :loading-state="{ icon: 'i-ph-circle-notch-bold', label: '' }"
                :empty-state="{ icon: 'i-ph-first-aid-duotone', label: isFieldMedic ? 'No field posts are assigned to you.' : 'No field posts.' }"
                :ui="{ thead: 'sticky top-0 bg-white z-10 shadow-sm', tbody: 'divide-y divide-gray-200', td: { padding: 'py-3' }, tr: { base: 'cursor-pointer hover:bg-gray-50' } }"
                class="h-full"
                @select="(row: Row) => navigateTo(`/field-posts/${row.id}`)"
            >
                <template #name-data="{ row }">
                    <div class="leading-5">
                        <p>{{ row.name }}</p>
                        <span class="text-sm font-mono">{{ row.code }}</span>
                    </div>
                </template>
                <template #areas-data="{ row }">
                    <span v-if="row.areas?.length">{{ row.areas.join(', ') }}</span>
                    <span v-else class="text-gray-300">–</span>
                </template>
                <template #open-data="{ row }">
                    <span class="font-mono">{{ row.open }}</span>
                </template>
                <template #stockAlerts-data="{ row }">
                    <UBadge v-if="row.stockAlerts" color="red" variant="subtle" :label="String(row.stockAlerts)" />
                    <span v-else class="text-gray-300">–</span>
                </template>
                <template #equipmentIssues-data="{ row }">
                    <UBadge v-if="row.equipmentIssues" color="orange" variant="subtle" :label="String(row.equipmentIssues)" />
                    <span v-else class="text-gray-300">–</span>
                </template>
                <template #status-data="{ row }">
                    <BadgeActiveStatus :active="row.is_active" />
                </template>
            </UTable>
        </div>
    </div>
</template>

<script setup lang="ts">
import { UserRole } from '~/types/user'
import { FIELD_POST_READ_ROLES, EquipmentCondition, InterventionStatus, type FieldPost } from '~/types/fieldPost'
import { lotStatus, todayIso } from '~/utils/fieldPosts'

definePageMeta({ layout: 'default', middleware: 'auth', roles: FIELD_POST_READ_ROLES })
useHead({ title: 'Field Posts' })

type Row = FieldPost & { open: number; stockAlerts: number; equipmentIssues: number }

const client = useSupabaseClient()
const auth = useAuthStore()

const isFieldMedic = computed(() => !!auth.hasRole(UserRole.fieldMedic))

const loading = ref(true)
const rows = ref<Row[]>([])

const columns = [
    { key: 'name', label: 'Name' },
    { key: 'areas', label: 'Areas', class: 'w-full' },
    { key: 'open', label: 'Open' },
    { key: 'stockAlerts', label: 'Stock Alerts', class: 'whitespace-nowrap' },
    { key: 'equipmentIssues', label: 'Equipment Issues', class: 'whitespace-nowrap' },
    { key: 'status', label: 'Status', class: 'w-px' },
]

onMounted(async () => {
    const [postsRes, openRes, lotsRes, equipmentRes] = await Promise.all([
        client.from('field_posts').select('id, name, code, areas, is_active').order('name'),
        client.from('interventions').select('post_id').eq('status', InterventionStatus.inProgress),
        client.from('stock_lots').select('post_id, qty_on_hand, expires_on'),
        client.from('field_post_equipment').select('post_id').neq('condition', EquipmentCondition.ok),
    ])

    const today = todayIso()
    const count = <T extends { post_id: string }>(data: T[] | null, postId: string, filter: (r: T) => boolean = () => true) =>
        (data ?? []).filter(r => r.post_id === postId && filter(r)).length

    const posts = (postsRes.data ?? []) as FieldPost[]
    rows.value = posts.map(post => ({
        ...post,
        open: count(openRes.data as { post_id: string }[] | null, post.id),
        stockAlerts: count(lotsRes.data as { post_id: string; qty_on_hand: number; expires_on: string | null }[] | null, post.id, l => lotStatus(l, today) !== 'ok'),
        equipmentIssues: count(equipmentRes.data as { post_id: string }[] | null, post.id),
    }))
    loading.value = false

    if (isFieldMedic.value && posts.length === 1) {
        await navigateTo(`/field-posts/${posts[0].id}`, { replace: true })
    }
})
</script>
